import { useEffect, useState } from 'react'
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select
} from '@mui/material'
import refundTransaction from './api'
import {
  MANUAL_REFUND_REASONS,
  refundDestinationNote,
  refundProblemMessage,
  refundReasonLabel
} from './utils'

const MUTED = 'text-gray-500 dark:text-gray-400'

export default function RefundDialog({
  open,
  transactionId,
  details = [],
  alternativePayment,
  walletShare,
  onClose,
  onRefunded
}) {
  const [reason, setReason] = useState(MANUAL_REFUND_REASONS[0])
  const [sending, setSending] = useState(false)
  const [problem, setProblem] = useState(null)

  useEffect(() => {
    if (!open) return
    setReason(MANUAL_REFUND_REASONS[0])
    setProblem(null)
  }, [open])

  const close = () => {
    if (!sending) onClose()
  }

  const submit = async () => {
    setSending(true)
    setProblem(null)
    const { ok, status, data } = await refundTransaction(transactionId, reason)
    setSending(false)
    if (ok) {
      onRefunded(data)
      return
    }
    const code = data?.code || (status === 404 ? 'not_found' : null)
    setProblem(refundProblemMessage(code, data || {}))
  }

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth='sm'>
      <DialogTitle>Reembolsar transacción</DialogTitle>
      <DialogContent>
        <div className='flex flex-col gap-4 pt-1'>
          <dl className='grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm'>
            <dt className={MUTED}>ID</dt>
            <dd className='font-mono break-all'>{transactionId}</dd>
            {details.map(([label, value]) => (
              <div key={label} className='contents'>
                <dt className={MUTED}>{label}</dt>
                <dd className='break-words'>{value || '-'}</dd>
              </div>
            ))}
          </dl>

          <label className='flex flex-col gap-1 text-sm'>
            <span className={MUTED}>Motivo</span>
            <Select
              size='small'
              value={reason}
              onChange={event => setReason(event.target.value)}
              disabled={sending}
              inputProps={{ 'aria-label': 'Motivo' }}
            >
              {MANUAL_REFUND_REASONS.map(option => (
                <MenuItem key={option} value={option}>
                  {refundReasonLabel(option)}
                </MenuItem>
              ))}
            </Select>
          </label>

          <p className={`text-sm ${MUTED}`}>
            {refundDestinationNote({ alternativePayment, walletShare })} En todos los casos se registra el
            evento Reembolso en Mixpanel.
          </p>

          {problem && <Alert severity='error'>{problem}</Alert>}
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <button
          className='px-3 py-2 rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-dk-hover disabled:opacity-50'
          onClick={close}
          disabled={sending}
        >
          Cancelar
        </button>
        <button
          className='px-3 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-50'
          onClick={submit}
          disabled={sending || !transactionId}
        >
          {sending ? 'Reembolsando…' : 'Reembolsar'}
        </button>
      </DialogActions>
    </Dialog>
  )
}
